import React, { useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Helmet } from 'react-helmet-async';

import SpinnerComponent from './Common/Spinners.js';
import LeftSidebar from './Common/LeftSidebar';

import MatchList from './Common/MatchList';

const HomeWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  font-size: 0.85em;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 2em;
  max-width: 25em;

  @media (max-width: 768px) {
    max-height: 100%;
    max-width: 100%;
  }
`;

const SectionTitle = styled.div`
  font-size: 2em;
  font-weight: bold;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 30em;
  margin: .5em 0 2em 0;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
  overflow-y: auto;
`;

const NewsItem = styled.div`
  padding: 1em 1em 1em 0;
  border-bottom: 1px #ccc dashed;

  &:last-of-type {
    border-bottom: none;
  }
`;

const AdditionalColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 20em;
  margin-top: 2em;
  margin-left: 2em;

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 1em;
    margin-left: 0;
  }
`;

const AdditionalColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AdditionalColumnListContainer = styled.div`
  margin-top: .5em;
  margin-bottom: 1em;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
  font-size: .9em;

  > div:first-of-type {
    border-top: none;
  }
`;

const MatchesTabsContainer = styled.div`
  flex: 1;
  margin-top: .5em;
  padding-top: .3em;
  border-top: 1px solid black;
`

const MatchesTabsInnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid rgb(204, 204, 204)
`

const MatchesTab = styled.div`
  flex: 1;
  background: ${props => props.selected ? '#228fd8' : 'white'};
  padding: .3em;
  color: ${props => props.selected ? 'white' : 'black'};
  font-size: .8em;
  font-weight: bold;
  text-align: center;
  cursor: pointer;
`

const QuickAccessRow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0;
  padding: .35em 0;
  border-top: 1px dashed #ccc;
`;

const QuickAccessName = styled.div`
  flex: 3.3;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const QuickAccessLinks = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-right: 1em;
  font-size: 0.85em;
`;

const Home = ({ AppStore }) => {
  const route = 'home';
  const didRouteChange = route !== AppStore.currentRoute.get();

  const quickAccessFetching = AppStore.quickAccessFetching.get();
  const quickAccessFetched = AppStore.quickAccessFetched.get();
  const quickAccessTournaments = AppStore.quickAccessTournaments;

  const upcomingMatches = AppStore.upcomingMatchesMap.all;
  const allUpcomingMatchesFetched = AppStore.allUpcomingMatchesFetched.get();
  const upcomingMatchesFetching = AppStore.upcomingMatchesFetching.get();

  const inProgressMatches = AppStore.inProgressMatchesMap.all;
  const allInProgressMatchesFetched = AppStore.allInProgressMatchesFetched.get();
  const inProgressMatchesFetching = AppStore.inProgressMatchesFetching.get();

  const recentMatches = AppStore.recentMatchesMap.all;
  const allRecentMatchesFetched = AppStore.allRecentMatchesFetched.get();
  const recentMatchesFetching = AppStore.recentMatchesFetching.get();

  const matchesTabInView = AppStore.homePageMatchesTabInView.get();

  const pageTitle = 'OE Valorant';
  const pageDescription = 'Oracle\'s Elixir Valorant';

  useEffect(() => {
    if (!quickAccessFetched && !quickAccessFetching) {
      AppStore.fetchQuickAccess();
    }

    if (!upcomingMatches && !upcomingMatchesFetching) {
      AppStore.fetchUpcomingMatches({});
    }

    if (!inProgressMatches && !inProgressMatchesFetching) {
      AppStore.fetchInProgressMatches({});
    }

    if (!recentMatches && !recentMatchesFetching) {
      AppStore.fetchRecentMatches({});
    }

    if (didRouteChange) {
      AppStore.setCurrentRoute(route);
    }
  }, [
    didRouteChange,
    AppStore,
    quickAccessFetching,
    quickAccessFetched,
    quickAccessTournaments,
    upcomingMatches,
    upcomingMatchesFetching,
    inProgressMatches,
    inProgressMatchesFetching,
    recentMatches,
    recentMatchesFetching,
    matchesTabInView
  ]);

  return (
    <>
      <Helmet>
        <title>OE Valorant</title>
        <meta
          name={pageTitle}
          content={pageDescription}
        />
      </Helmet>
      <LeftSidebar />
      <HomeWrapper className="mainContent">
        <MainContent>
          <SectionTitle>OE - VALORANT</SectionTitle>
          <SectionWrapper>
            <p style={{ 'marginTop': '0.8em' }}>Welcome to the future of VALORANT esports analytics.</p>
            <p>Please report feedback and/or bugs <a href='https://discord.gg/KY6XHaF' target="_blank" rel="noopener noreferrer">via Discord</a>.</p>
          </SectionWrapper>
          <SectionTitle>Release Notes</SectionTitle>
          <SectionWrapper>
            <NewsItem>
              <b>August 1, 2024</b> - OE VALORANT is now live. Enjoy!
            </NewsItem>
            <NewsItem>
              <b>July 25, 2024 <em>[beta]</em></b> - Added plant rate (PL%) and postplant win rate (PP%) to team stats tables.
            </NewsItem>
            <NewsItem>
              <b>July 18, 2024 <em>[beta]</em></b> - Added basic player stats by split tab to player profiles.
            </NewsItem>
            <NewsItem>
              <b>June 28, 2024 <em>[beta]</em></b> - Added map filter to player and team stats pages, and agent filter to player stats.
            </NewsItem>
            <NewsItem>
              <b>June 24, 2024 <em>[beta]</em></b> - Added a brief explainer article for <a href='/rar'>Rounds Above Replacement (RAR)</a>.
            </NewsItem>
            <NewsItem>
              <b>June 20, 2024 <em>[beta]</em></b> - Added live matches widget to home page, with tab navigation to choose which set of matches to view.
            </NewsItem>
            <NewsItem>
              <b>June 13, 2024 <em>[beta]</em></b> - A filter has been added to player profile Agent Stats pages that lets you select a starting year for aggregating the agent stats.
            </NewsItem>
            <NewsItem>
              <b>May 3, 2024 <em>[beta]</em></b> - Upcoming Matches and Recent Matches widgets have been added to the Home page and to player profile pages.
              <br /><br />
              Work is continuing on data cleanliness and completeness. Please report any issues you see on the Discord.
            </NewsItem>
          </SectionWrapper>
        </MainContent>
        <AdditionalColumnWrapper>
          <AdditionalColumnContainer>
            <SectionTitle>
              Matches
            </SectionTitle>
            <MatchesTabsContainer>
              <MatchesTabsInnerContainer>
                <MatchesTab
                  selected={matchesTabInView === 'upcoming'}
                  onClick={() => AppStore.setHomePageMatchesTabInView('upcoming')}
                >
                  Upcoming
                </MatchesTab>
                <MatchesTab
                  selected={matchesTabInView === 'inProgress'}
                  onClick={() => AppStore.setHomePageMatchesTabInView('inProgress')}
                >
                  Live
                </MatchesTab>
                <MatchesTab
                  selected={matchesTabInView === 'recent'}
                  onClick={() => AppStore.setHomePageMatchesTabInView('recent')}
                >
                  Recent
                </MatchesTab>
              </MatchesTabsInnerContainer>
            </MatchesTabsContainer>
            {matchesTabInView === 'upcoming' ? (
              <AdditionalColumnListContainer>
                <MatchList
                  type='upcoming'
                  view='all'
                  matchList={upcomingMatches}
                  allFetched={allUpcomingMatchesFetched}
                  fetching={upcomingMatchesFetching}
                />
              </AdditionalColumnListContainer>
            ) : matchesTabInView === 'inProgress' ? (
              <AdditionalColumnListContainer>
                <MatchList
                  type='inProgress'
                  view='all'
                  matchList={inProgressMatches}
                  allFetched={allInProgressMatchesFetched}
                  fetching={inProgressMatchesFetching}
                />
              </AdditionalColumnListContainer>
            ) : (
              <AdditionalColumnListContainer>
                <MatchList
                  type='recent'
                  view='all'
                  matchList={recentMatches}
                  allFetched={allRecentMatchesFetched}
                  fetching={recentMatchesFetching}
                />
              </AdditionalColumnListContainer>
            )}
          </AdditionalColumnContainer>
        </AdditionalColumnWrapper>
        <AdditionalColumnWrapper>
          <AdditionalColumnContainer>
            <SectionTitle>
              Quick Access
            </SectionTitle>
            <AdditionalColumnListContainer>
              {quickAccessTournaments.length > 0 ? (
                quickAccessTournaments.map(tournament => {
                  return (
                    <QuickAccessRow
                      key={tournament.tournamentId}
                    >
                      <QuickAccessName>
                        {`${tournament.leagueName} - ${tournament.tournamentName}`}
                      </QuickAccessName>
                      <QuickAccessLinks>
                        <Link to={`/stats/players/byTournament/${tournament.tournamentId}`}>
                          Players
                        </Link>
                        |
                        <Link to={`/stats/teams/byTournament/${tournament.tournamentId}`}>
                          Teams
                        </Link>
                      </QuickAccessLinks>
                    </QuickAccessRow>
                  )
                })
              ) : (
                <SpinnerComponent />
              )}
            </AdditionalColumnListContainer>
          </AdditionalColumnContainer>
        </AdditionalColumnWrapper>
      </HomeWrapper>
    </>
  );
};

export default inject("AppStore")(observer(Home));
